import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Text, useTranslator } from '@eo-locale/react';
import { Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@guest-widgets/core';
import Fade from '@mui/material/Fade';

import { useProducts } from '../productsContext';

export interface BackToTopProps {
  containerEl: RefObject<HTMLDivElement>;
}

const BackToTop = ({ containerEl }: BackToTopProps) => {
  const { products, totalCount, pageSize } = useProducts();
  const { translate } = useTranslator();
  const scrollYPosition = useRef(0);
  const timer = useRef(0);
  const [isVisible, setIsVisible] = useState(false);
  const backToTopRef = useRef<HTMLDivElement>(null);

  const onScroll = () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
    }

    timer.current = window.setTimeout(() => {
      const scroll = backToTopRef.current?.offsetTop;
      if (!scroll || !containerEl.current) return;

      const isOnTop = containerEl.current.getBoundingClientRect().top >= 0;
      const hasScrolledUp = scroll > scrollYPosition.current;

      setIsVisible(hasScrolledUp && !isOnTop);
      scrollYPosition.current = scroll;
    }, 100);
  };

  const onClick = () => {
    containerEl.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    scrollYPosition.current = 0;
  };

  useEffect(() => {
    if (!products.value) return;
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll);
  }, [!!products.value]);

  if (!products.value || !totalCount || totalCount <= pageSize) return null;

  return (
    <Container ref={backToTopRef}>
      <Fade in={isVisible}>
        <Tooltip
          aria-label={translate('product-catalog.pagination.back-to-top')}
          title={<Text id="product-catalog.pagination.back-to-top" />}
          placement="top"
        >
          <Fab color="secondary" onClick={onClick}>
            <KeyboardArrowUpIcon />
          </Fab>
        </Tooltip>
      </Fade>
    </Container>
  );
};

const StyledButton = styled(Button)({
  boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.25)',
});

const Container = styled('div')(({ theme: { spacing }, widgetArea }) => ({
  position: 'sticky',
  right: spacing(8),
  bottom: 16,
  textAlign: 'right',
  marginTop: spacing(-9),
  paddingRight: spacing(widgetArea.basedOnSize(0, 1, 3)),
  zIndex: 5,
}));

export default BackToTop;
